import { motion } from "framer-motion";
import { ShoppingBag, ShieldCheck, Zap, CreditCard, Check } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

export default function HowItOrderWorks({ networkName, accentColor }) {
  const { t } = useLanguage();

  const steps = [
    {
      icon: ShoppingBag,
      title: t("network.order_step1_title", "1. Choisissez votre pack"),
      desc: t("network.order_step1_desc", "Sélectionnez la quantité qui vous convient, puis renseignez le lien ou le nom d'utilisateur de votre compte"),
    },
    {
      icon: CreditCard,
      title: t("network.order_step2_title", "2. Paiement 100% sécurisé"),
      desc: t("network.order_step2_desc", "Réglez en toute sécurité par carte bancaire, Google Pay ou Apple Pay, sans jamais communiquer votre mot de passe"),
    },
    {
      icon: Zap,
      title: t("network.order_step3_title", "3. Livraison instantanée"),
      desc: t("network.order_step3_desc", "Votre commande démarre automatiquement : vous commencez à recevoir votre pack en quelques minutes"),
    },
  ];

  return (
    <div className="max-w-5xl mx-auto mb-20" data-testid="how-it-order-works">
      <h2 className="font-heading text-2xl font-bold text-white mb-2 text-center">
        {t("network.order_how_title", "Comment commander vos")} {networkName} ?
      </h2>
      <p className="text-sm text-zinc-500 text-center mb-10 max-w-lg mx-auto">
        {t("network.order_how_subtitle", "3 étapes simples, sans mot de passe, pour booster votre compte dès aujourd'hui.")}
      </p>

      <div className="grid md:grid-cols-3 gap-6 relative">
        <div className="hidden md:block absolute top-8 left-[16.5%] right-[16.5%] h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
        {steps.map((s, i) => (
          <motion.div
            key={s.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ delay: i * 0.12, duration: 0.4 }}
            className="relative rounded-2xl border border-white/8 bg-zinc-900/60 p-6 text-center" data-testid={`order-step-${i + 1}`}
          >
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 relative z-10"
              style={{ background: `linear-gradient(135deg, ${accentColor}, #a855f7)`, boxShadow: `0 8px 20px -6px ${accentColor}88` }}
            >
              <s.icon className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-heading text-base font-semibold text-white mb-2">{s.title}</h3>
            <p className="text-sm text-zinc-500 leading-relaxed">{s.desc}</p>
            {i === 1 && (
              <div className="flex items-center justify-center gap-1.5 mt-4">
                {["CB", "Google Pay", "Apple Pay"].map((m) => (
                  <span key={m} className="text-[10px] font-semibold px-2 py-1 rounded-md bg-white/8 text-zinc-300 border border-white/10">{m}</span>
                ))}
              </div>
            )}
            {i === 2 && (
              <>
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ delay: 0.45, duration: 0.5, type: "spring", stiffness: 260, damping: 15 }}
                  className="absolute -top-3 -right-3 w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/40 z-20"
                  data-testid="order-step3-checkmark"
                >
                  <Check className="w-5 h-5 text-white" strokeWidth={3} />
                </motion.div>
                <div className="inline-flex items-center gap-1.5 mt-4 text-[11px] font-semibold" style={{ color: accentColor }}>
                  <ShieldCheck className="w-3.5 h-3.5" /> {t("network.order_step3_badge", "Aucun mot de passe requis")}
                </div>
              </>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}
